import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { AccessTokenResponse } from "./models/gateway/access-token-response.model";
import { OauthTokenService } from "./services/ouath-token.service";
import { HttpUtil } from "./_util/http-util";
import { Observable } from "rxjs";
import storageX from "./storageCore/storageX";
import { STORAGE_KEY_ACCESS_TOKEN_WSO2 } from "./storageCore/constStorageKeys";
import { KeycloakAuthService } from "./services/keycloak-auth.service";
import { AuthenticationService } from "./services/authentication.service";
import { refreshWso2TokenOnce } from "./utils/wso2-token-refresh";
import { ensureDeviceUuid } from "./utils/device-uuid";

// O idle da sessão SSO no Keycloak é de 30 min: o refresh token expira se
// ficar 30 min sem uso. Renovar a cada 4 min mantém a sessão sempre viva e
// nunca deixa o refresh token chegar perto do vencimento.
const FOUR_MIN_IN_MS = 4 * 60 * 1000;
const THIRTY_MIN_IN_MS = 30 * 60 * 1000;

@Injectable({
  providedIn: "root",
})
export class StartAppService {
  public intervalTimeInMilliseconds = FOUR_MIN_IN_MS;
  static reload$ = new EventEmitter<any>();
  loaded: boolean = false;
  refresh$ = new EventEmitter<any>();
  oAuth = new AccessTokenResponse();
  clientId = "";
  private tokenRefreshTimerId: ReturnType<typeof setInterval> | null = null;
  private lastWso2RefreshAt = Date.now();

  constructor(
    public oAuthService: OauthTokenService,
    public http: HttpClient,
    public httpUtil: HttpUtil,
    private keycloakAuthService: KeycloakAuthService,
    private authenticationService: AuthenticationService
  ) {}

  public initApp(): Promise<any> {
    return (async (): Promise<boolean> => {
      console.info("Inicializando aplicação");
      console.info("[APP][init] contexto inicial", {
        href: window.location.href,
        search: window.location.search,
      });
      try {
        console.info("Realizando autenticação junto ao gateway (WSO2)...");
        ensureDeviceUuid();
        await this.obtainGatewayTokenOnce();
        console.info("Autenticação junto ao gateway (WSO2) concluída");
      } catch (error) {
        console.error("Falha ao autenticar junto ao gateway", error);
        return false;
      }

      try {
        await this.keycloakAuthService.init();
      } catch (error) {
        console.error("Falha ao inicializar Keycloak", error);
        console.error("[APP][init] falha no init do keycloak", {
          href: window.location.href,
          search: window.location.search,
          hasCodeState: this.keycloakAuthService.hasAuthCallbackParams(),
        });
        return false;
      }

      console.info("[APP][init] keycloak init concluido", {
        isAuthenticated: this.keycloakAuthService.isAuthenticated(),
        hasCodeState: this.keycloakAuthService.hasAuthCallbackParams(),
      });

      if (this.keycloakAuthService.isAuthenticated()) {
        try {
          console.info("[APP][init] iniciando syncUserFromKeycloak");
          await this.authenticationService.syncUserFromKeycloak();
          console.info("[APP][init] syncUserFromKeycloak concluido");
        } catch (error) {
          console.error("Falha ao sincronizar usuario do Keycloak", error);
        }
      }

      this.initListenerToken();
      return true;
    })();
  }

  public initListenerToken(): void {
    if (this.tokenRefreshTimerId) {
      clearInterval(this.tokenRefreshTimerId);
      this.tokenRefreshTimerId = null;
    }

    const intervalMs = this.intervalTimeInMilliseconds;
    const nextAt = new Date(Date.now() + intervalMs);
    console.log(
      `[TOKEN] Listener silencioso ATIVO. Intervalo=${intervalMs / 1000 / 60}min. Próximo refresh ~ ${nextAt.toLocaleTimeString()}`
    );

    this.tokenRefreshTimerId = setInterval(() => {
      void this.refreshTokensSilently();
    }, intervalMs);
  }

  private async refreshTokensSilently(): Promise<void> {
    console.log(
      "----> INICIANDO REFRESH SILENCIOSO DE TOKENS: " + new Date().toLocaleString() + " <----"
    );

    // WSO2 (client_credentials) expira em ~1h; renovar a cada 30 min basta
    if (Date.now() - this.lastWso2RefreshAt >= THIRTY_MIN_IN_MS) {
      try {
        await refreshWso2TokenOnce();
        this.lastWso2RefreshAt = Date.now();
        console.log("[TOKEN] WSO2 atualizado com sucesso (sem reload)");
      } catch (error) {
        console.error("[TOKEN] Falha ao atualizar WSO2", error);
      }
    }

    // -1 força o refresh mesmo com access token ainda válido: cada ciclo gera
    // um refresh token novo e reseta o idle da sessão SSO no servidor
    try {
      await this.keycloakAuthService.updateToken(-1);
      console.log("[TOKEN] Keycloak atualizado com sucesso (sem reload)");
    } catch (error) {
      console.error("[TOKEN] Falha ao atualizar Keycloak", error);
    }

    const nextAt = new Date(Date.now() + this.intervalTimeInMilliseconds);
    console.log(`[TOKEN] Próximo refresh silencioso ~ ${nextAt.toLocaleTimeString()}`);
  }

  private obtainGatewayTokenOnce(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.oAuthService.getGatewayCredentials().subscribe(
        (gatewayCredentials) => {
          this.clientId = gatewayCredentials.clientId;
          this.httpUtil.clientId = gatewayCredentials.clientId;
          this.oAuthService.generateGrantToken().subscribe(
            (grantCodeResponse) => {
              storageX
                .whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_WSO2)
                .set(grantCodeResponse.access_token);
              resolve();
            },
            (error) => {
              console.error(error);
              reject(error);
            }
          );
        },
        (error) => {
          console.error(error);
          reject(error);
        }
      );
    });
  }

  private loadGatewayCredentials(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.obtainGatewayTokenOnce()
        .then(() => {
          subscriber.next(true);
          subscriber.complete();
        })
        .catch((err) => {
          subscriber.error(err);
        });
    });
  }
}
